import React from 'react'
import { components } from 'react-select'

import { FormItemProps } from '@/components/Form/types'
import ImageComponent from '@/components/UiElements/ImageComponent'
import getCategories from '@/services/CategoryService/getCategories'
import getDesignTemplates from '@/services/DesignTemplateService/getDesignTemplates'

/**
 * What a section may offer customers: graphic categories and design templates
 * that are not marked available everywhere.
 *
 * Shared by the 3D and 2D section forms so the two cannot drift. Both are saved
 * inside the section payload, which is what keeps the links alive across the
 * section rewrite every profile save performs.
 */

const getGraphicCategoryImage = (category: any): string | null => {
  return category?.data?.img || category?.img || category?.category_image_url || null
}

const getGraphicCategoryMeta = (category: any): string => {
  const status = category?.isActive ? 'Active' : 'Inactive'
  const elements = category?.childCount
    ? `${category.childCount} element${category.childCount > 1 ? 's' : ''}`
    : 'Empty'
  const subcategories = category?.childCategory
    ? `${category.childCategory} subcategor${category.childCategory > 1 ? 'ies' : 'y'}`
    : null
  const home = category?.showOnHome ? 'Show on home' : null

  return [status, home, elements, subcategories].filter(Boolean).join(' | ')
}

const GraphicCategoryImage = ({ category, size = 'md' }: { category: any; size?: 'sm' | 'md' }) => {
  const image = getGraphicCategoryImage(category)
  const className = size === 'sm' ? 'w-[18px] h-[18px] min-w-[18px]' : 'w-[36px] h-[36px] min-w-[36px]'

  return (
    <div
      className={`${className} rounded-sm bg-gray-100 border border-gray-200 overflow-hidden flex items-center justify-center`}
    >
      {image ? (
        <ImageComponent src={image} className="!w-full !h-full" style={{ width: '100%', height: '100%' }} />
      ) : (
        <span className="font-icon-regular text-gray-300 text-[12px]">image</span>
      )}
    </div>
  )
}

const GraphicCategoryOption = (props: any) => {
  const category = props.data?.data || props.data

  return (
    <components.Option {...props}>
      <div className="flex items-center gap-sm min-w-0">
        <GraphicCategoryImage category={category} />
        <div className="flex flex-col min-w-0">
          <span className="truncate">{props.data?.label}</span>
          <span className="text-text-xs text-gray-400 truncate">{getGraphicCategoryMeta(category)}</span>
        </div>
      </div>
    </components.Option>
  )
}

const GraphicCategoryMultiValueLabel = (props: any) => {
  const category = props.data?.data || props.data

  return (
    <components.MultiValueLabel {...props}>
      <div className="flex items-center gap-xs min-w-0">
        <GraphicCategoryImage category={category} size="sm" />
        <span className="truncate">{props.data?.label}</span>
      </div>
    </components.MultiValueLabel>
  )
}

const getDesignTemplateMeta = (template: any): string => {
  const status = template?.isActive ? 'Active' : 'Inactive'
  const fields = template?.placeholderCount
    ? `${template.placeholderCount} field${template.placeholderCount > 1 ? 's' : ''} to fill in`
    : 'No fields to fill in'
  const design = template?.elementCount ? `${template.elementCount} elements` : 'Not designed yet'
  return [status, fields, design].filter(Boolean).join(' | ')
}

const DesignTemplateImage = ({ template, size = 'md' }: { template: any; size?: 'sm' | 'md' }) => {
  const image = template?.thumbnail
  const className = size === 'sm' ? 'w-[18px] h-[18px] min-w-[18px]' : 'w-[36px] h-[36px] min-w-[36px]'

  return (
    <div
      className={`${className} rounded-sm bg-gray-100 border border-gray-200 overflow-hidden flex items-center justify-center`}
    >
      {image ? (
        <ImageComponent src={image} className="!w-full !h-full" style={{ width: '100%', height: '100%' }} />
      ) : (
        <span className="font-icon-regular text-gray-300 text-[12px]">image</span>
      )}
    </div>
  )
}

const DesignTemplateOption = (props: any) => {
  const template = props.data?.data || props.data

  return (
    <components.Option {...props}>
      <div className="flex items-center gap-sm min-w-0">
        <DesignTemplateImage template={template} />
        <div className="flex flex-col min-w-0">
          <span className="truncate">{props.data?.label}</span>
          <span className="text-text-xs text-gray-400 truncate">{getDesignTemplateMeta(template)}</span>
        </div>
      </div>
    </components.Option>
  )
}

const DesignTemplateMultiValueLabel = (props: any) => {
  const template = props.data?.data || props.data

  return (
    <components.MultiValueLabel {...props}>
      <div className="flex items-center gap-xs min-w-0">
        <DesignTemplateImage template={template} size="sm" />
        <span className="truncate">{props.data?.label}</span>
      </div>
    </components.MultiValueLabel>
  )
}

interface AssignmentOptions {
  /** Optional visibility rule, used by the 3D form to wait for a target object. */
  condition?: FormItemProps['condition']
}

export const getSectionAssignmentItems = ({ condition }: AssignmentOptions = {}): Array<FormItemProps> => [
  {
    name: 'graphicCategoryIds',
    label: 'Specific Graphic Category',
    help: 'Select the non-generic graphic categories assigned specifically to this section.',
    placeholder: 'Select category',
    type: 'Select',
    itemOptions: {
      fetch: async () => {
        const list = await getCategories()
        return list
          .filter((c) => c.type === 'category' && !c.isGeneric)
          .map((c) => ({
            label: c.name,
            value: c.id,
            data: c,
          }))
      },
      features: { isMulti: true },
      clearable: true,
      components: {
        Option: GraphicCategoryOption,
        MultiValueLabel: GraphicCategoryMultiValueLabel,
      },
    },
    defaultValue: [],
    classNames: { container: 'col-span-12 ' },
    condition,
  },
  {
    name: 'designTemplateIds',
    label: 'Specific Design Templates',
    help: 'Select the design templates offered on this section. Templates marked as available everywhere already appear on every section and are not listed here.',
    placeholder: 'Select template',
    type: 'Select',
    itemOptions: {
      fetch: async () => {
        const list = await getDesignTemplates()
        return list
          .filter((t) => !t.isGeneric)
          .map((t) => ({
            label: t.name,
            value: t.id,
            data: t,
          }))
      },
      features: { isMulti: true },
      clearable: true,
      components: {
        Option: DesignTemplateOption,
        MultiValueLabel: DesignTemplateMultiValueLabel,
      },
    },
    defaultValue: [],
    classNames: { container: 'col-span-12 ' },
    condition,
  },
]
